import { Button, DatePicker, Drawer, Form, Input, Select, Space } from "antd";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { BankAccountGroupedLookup } from "../lookup/BankAccountGroupedLookup";
import { UI_DATE_FORMAT } from "../utils/date";

type FilterValues = {
  search?: string;
  paymentType?: string;
  paymentStatus?: string;
  paidOf?: string;
  type?: string;
  bankAccountId?: string | number | null;
  dateRange?: [Dayjs, Dayjs];
};

export function FilterDrawer({
  open,
  onClose,
  onApply,
  onReset,
  moduleKey = "DailyEntry",
  values
}: {
  open: boolean;
  onClose: () => void;
  onApply: (values: FilterValues) => void;
  onReset?: () => void;
  moduleKey?: string;
  values?: Record<string, unknown>;
}) {
  const [form] = Form.useForm<FilterValues>();
  const [bankAccountLabel, setBankAccountLabel] = useState<string>();
  const [bankLabel, setBankLabel] = useState<string>();
  const isDaily = moduleKey === "DailyEntry";
  const isCash = moduleKey === "CashEntry";
  const isStatement = moduleKey === "StatementEntry";

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      search: values?.search as string,
      paidOf: values?.paidOf as string,
      paymentType: values?.paymentType as string,
      paymentStatus: values?.paymentStatus as string,
      type: values?.type as string,
      bankAccountId: values?.bankAccountId as string | number | null
    });
  }, [form, open, values]);

  return (
    <Drawer
      title="Filters"
      width={420}
      open={open}
      onClose={onClose}
      extra={<Button onClick={() => { form.resetFields(); setBankAccountLabel(undefined); setBankLabel(undefined); onReset?.(); }}>Reset</Button>}
    >
      <Form layout="vertical" form={form} onFinish={(values) => { onApply(values); onClose(); }}>
        <Form.Item name="search" label="Search">
          <Input placeholder={isStatement ? "Search particulars or reference" : "Search remarks, party, category"} />
        </Form.Item>
        <Form.Item name="dateRange" label="Date Range">
          <DatePicker.RangePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} />
        </Form.Item>
        {isCash ? (
          <Form.Item name="paidOf" label="Paid Of">
            <Input placeholder="Filter by paid of" />
          </Form.Item>
        ) : null}
        {isDaily || isCash ? (
          <Form.Item name="paymentType" label="Payment Type">
            <Select allowClear options={[{ value: "Cash", label: "Cash" }, { value: "Bank", label: "Bank" }]} />
          </Form.Item>
        ) : null}
        {isDaily ? (
          <Form.Item name="paymentStatus" label="Status">
            <Select allowClear options={[{ value: "Pending", label: "Pending" }, { value: "Paid", label: "Paid" }]} />
          </Form.Item>
        ) : null}
        {isStatement ? (
          <>
            <Form.Item name="type" label="Type">
              <Select allowClear options={[{ value: "Debit", label: "Debit" }, { value: "Credit", label: "Credit" }]} />
            </Form.Item>
            <Form.Item name="bankAccountId" label="Bank Account">
              <BankAccountGroupedLookup
                bankLabel={bankLabel}
                accountLabel={bankAccountLabel}
                onSelect={(value) => {
                  form.setFieldValue("bankAccountId", value.bankAccountId);
                  setBankLabel(value.bankLabel);
                  setBankAccountLabel(value.bankAccountLabel);
                }}
              />
            </Form.Item>
          </>
        ) : null}
        <Space>
          <Button type="primary" htmlType="submit">Apply</Button>
          <Button onClick={onClose}>Cancel</Button>
        </Space>
      </Form>
    </Drawer>
  );
}
